import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import {
  LayoutDashboard, Calculator, Users, Package, Route as RouteIcon,
  DollarSign, Ruler, UserCog, Code, LogOut, Truck, Globe, FileText, Tag, ClipboardList,
  Warehouse as WarehouseIcon, Boxes, CalendarClock, Globe2, MapPin, AlertTriangle,
  Landmark, DatabaseBackup, Palette, Navigation, ScanSearch, Layers, PackageCheck,
  Menu, X, PhoneCall, Star,
} from "lucide-react";
import NotificationsBell from "@/components/NotificationsBell";

const LANGS = [
  { code: "es", label: "ES" },
  { code: "en", label: "EN" },
  { code: "ar", label: "AR" },
];

const ROLE_COLORS = {
  admin: "bg-indigo-100 text-indigo-700",
  ventas: "bg-blue-100 text-red-700",
  operaciones: "bg-amber-100 text-amber-700",
  sucursal: "bg-fuchsia-100 text-fuchsia-700",
  cliente: "bg-emerald-100 text-emerald-700",
  almacen: "bg-orange-100 text-orange-700",
  contable: "bg-teal-100 text-teal-700",
  conductor: "bg-lime-100 text-lime-700",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, lang, setLang, dir } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const role = user.role;

  // Close mobile drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  const items = [
    { to: "/", icon: LayoutDashboard, label: t("dashboard"), roles: ["admin","ventas","operaciones","sucursal","cliente","almacen","contable","conductor"] },
    { to: "/mi-ruta", icon: Navigation, label: t("my_route"), roles: ["conductor"] },
    { to: "/cotizar", icon: Calculator, label: t("quote_shipping"), roles: ["admin","ventas","sucursal","cliente"] },
    { to: "/historial", icon: FileText, label: t("history"), roles: ["admin","ventas","sucursal","cliente"] },
    { to: "/clientes", icon: Users, label: t("clients"), roles: ["admin","ventas","sucursal"] },
    { to: "/ventas", icon: PhoneCall, label: t("sales_crm") || "Ventas · CRM", roles: ["admin","ventas","sucursal"] },
    { to: "/envios", icon: Package, label: t("shipments"), roles: ["cliente"] },
    // ==== Flujo de grupaje, en orden ====
    { to: "/etiquetas", icon: Tag, label: `1 · ${t("packages")}`, roles: ["admin","operaciones","ventas","sucursal","almacen"] },
    { to: "/salidas-oficina", icon: Truck, label: `2 · ${t("office_exits")}`, roles: ["admin","operaciones","sucursal","almacen","ventas"] },
    { to: "/recepcion", icon: PackageCheck, label: `3 · ${t("hub_reception")}`, roles: ["admin","operaciones","almacen"] },
    { to: "/masters", icon: Layers, label: `4 · ${t("masters")}`, roles: ["admin","operaciones","almacen","sucursal","ventas"] },
    { to: "/trazabilidad", icon: ScanSearch, label: `5 · ${t("trace")}`, roles: ["admin","operaciones","almacen","sucursal","ventas","contable"] },
    { to: "/incidencias", icon: AlertTriangle, label: `6 · ${t("incidents")}`, roles: ["admin","operaciones","almacen","sucursal","ventas"] },
    // ==== Almacén y transporte ====
    { to: "/inventario", icon: Boxes, label: t("inventory"), roles: ["admin","almacen","operaciones","sucursal"] },
    { to: "/ubicaciones", icon: MapPin, label: t("wms_locations"), roles: ["admin","almacen"] },
    { to: "/programacion", icon: CalendarClock, label: t("voyages"), roles: ["admin","operaciones","ventas","sucursal","almacen"] },
    { to: "/albaranes", icon: FileText, label: t("albaranes"), roles: ["admin","operaciones","sucursal","almacen"] },
    { to: "/conductores", icon: Truck, label: t("fleet"), roles: ["admin","operaciones","sucursal"] },
    { to: "/contabilidad", icon: Landmark, label: t("accounting"), roles: ["admin","contable","sucursal"] },
    { to: "/mis-facturas", icon: FileText, label: t("my_invoices"), roles: ["cliente"] },
    { to: "/rutas", icon: RouteIcon, label: t("own_routes"), roles: ["admin"] },
    { to: "/precios", icon: DollarSign, label: t("prices_margins"), roles: ["admin"] },
    { to: "/precios-especiales", icon: Star, label: t("client_special_pricing") || "Precios especiales", roles: ["admin"] },
    { to: "/volumen", icon: Ruler, label: t("volumetric_rules"), roles: ["admin"] },
    { to: "/paises", icon: Globe2, label: t("countries"), roles: ["admin"] },
    { to: "/almacenes", icon: WarehouseIcon, label: t("warehouses"), roles: ["admin"] },
    { to: "/usuarios", icon: UserCog, label: t("users"), roles: ["admin"] },
    { to: "/backups", icon: DatabaseBackup, label: t("backups"), roles: ["admin"] },
    { to: "/ajustes-facturacion", icon: Palette, label: t("invoice_settings"), roles: ["admin"] },
    // ==== Histórico (pantallas anteriores al flujo de grupaje) ====
    { to: "/envios", icon: Package, label: `${t("shipments")} · ${t("legacy")}`, roles: ["admin"] },
    { to: "/manifiestos", icon: ClipboardList, label: `${t("manifests")} · ${t("legacy")}`, roles: ["admin"] },
    { to: "/api-docs", icon: Code, label: t("api"), roles: ["admin"] },
  ].filter(i => i.roles.includes(role));

  const currentTitle = items.find(i => i.to === loc.pathname)?.label || t("dashboard");

  const doLogout = async () => { await logout(); nav("/login"); };

  const sideBorder = dir === "rtl" ? "border-s" : "border-e";

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="app-layout">
      {/* Backdrop for mobile drawer */}
      {drawerOpen && (
        <div
          data-testid="drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 md:hidden"
        />
      )}

      <aside
        data-testid="sidebar"
        className={
          `w-64 bg-[#0B0F19] text-white flex-col fixed h-full z-50 ${sideBorder} border-slate-800 transition-transform duration-200 ` +
          `md:flex md:translate-x-0 ` +
          (drawerOpen
            ? "flex translate-x-0"
            : (dir === "rtl" ? "flex translate-x-full md:translate-x-0" : "flex -translate-x-full md:translate-x-0"))
        }
      >
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg p-1.5 shrink-0">
              <img src="/logo.png" alt="Meos" className="h-8 w-auto" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-lg leading-tight">MEOS CARGO</div>
              <div className="text-xs text-slate-400 uppercase tracking-wider truncate">{t(role) || role}{user.oficina ? ` · ${user.oficina}` : ""}</div>
            </div>
            <button
              data-testid="drawer-close-btn"
              onClick={() => setDrawerOpen(false)}
              className="md:hidden text-slate-400 hover:text-white p-1"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-3 overflow-y-auto space-y-1" data-testid="sidebar-nav">
          {items.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.to === "/"}
              data-testid={`nav-${i.to.replace("/","") || "dashboard"}`}
              className={({isActive}) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
            >
              <i.icon className="w-4 h-4 shrink-0" />
              <span>{i.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center font-bold text-sm">
              {(user.fullname || user.username)[0].toUpperCase()}
            </div>
            <div className="overflow-hidden">
              <div className="text-sm font-medium truncate">{user.fullname}</div>
              <div className="text-xs text-slate-500 truncate">{user.email}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" /> {t("logout")}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 md:ms-64">
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center sticky top-0 z-30 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              data-testid="drawer-open-btn"
              onClick={() => setDrawerOpen(true)}
              className="md:hidden p-2 -ms-2 text-slate-700 hover:bg-slate-100 rounded-lg shrink-0"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="font-display text-base sm:text-xl font-semibold text-slate-900 truncate">{currentTitle}</h1>
              <p className="text-xs text-slate-500 hidden sm:block">
                {user.fullname} ·{" "}
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${ROLE_COLORS[role]}`}>
                  {t(role) || role}
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <NotificationsBell />
            <div className="hidden sm:flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="lang-switcher">
              <Globe className="w-4 h-4 text-slate-400 mx-1" />
              {LANGS.map(l => (
                <button
                  key={l.code}
                  data-testid={`lang-btn-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                    lang === l.code ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-900"
                  }`}
                >{l.label}</button>
              ))}
            </div>
            {/* Compact mobile lang selector */}
            <select
              data-testid="lang-select-mobile"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="sm:hidden text-xs font-semibold bg-slate-100 rounded-md px-2 py-1.5 outline-none"
            >
              {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
        </header>

        <div className="p-4 sm:p-6" data-testid="page-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
